import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Nav } from './components/Nav';
import { Footer } from './components/Footer';
import { RouteMeta } from './components/RouteMeta';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RouteFallback } from './components/RouteFallback';

// Route-based code splitting: each page ships as its own chunk. Named exports are
// adapted to default here. The redesign adds the "app demo" screens (Report,
// Plan, Progress, Tools) alongside the marketing pages.
const Landing = lazy(() => import('./pages/Landing').then((m) => ({ default: m.Landing })));
const Scan = lazy(() => import('./pages/Scan').then((m) => ({ default: m.Scan })));
const Report = lazy(() => import('./pages/Report').then((m) => ({ default: m.Report })));
const Method = lazy(() => import('./pages/Method').then((m) => ({ default: m.Method })));
const Privacy = lazy(() => import('./pages/Privacy').then((m) => ({ default: m.Privacy })));
const Pricing = lazy(() => import('./pages/Pricing').then((m) => ({ default: m.Pricing })));
const Guides = lazy(() => import('./pages/Guides').then((m) => ({ default: m.Guides })));
const Plan = lazy(() => import('./pages/Plan').then((m) => ({ default: m.Plan })));
const Progress = lazy(() => import('./pages/Progress').then((m) => ({ default: m.Progress })));
const Tools = lazy(() => import('./pages/Tools').then((m) => ({ default: m.Tools })));
const Account = lazy(() => import('./pages/Account').then((m) => ({ default: m.Account })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then((m) => ({ default: m.ResetPassword })));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail').then((m) => ({ default: m.VerifyEmail })));
const Admin = lazy(() => import('./pages/Admin').then((m) => ({ default: m.Admin })));
const Download = lazy(() => import('./pages/Download').then((m) => ({ default: m.Download })));
const PrivacyPolicy = lazy(() =>
  import('./pages/legal/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy })),
);
const Terms = lazy(() => import('./pages/legal/Terms').then((m) => ({ default: m.Terms })));
const NotFound = lazy(() => import('./pages/NotFound').then((m) => ({ default: m.NotFound })));

export function App() {
  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <RouteMeta />
      <Nav />
      <main id="main-content" tabIndex={-1}>
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/scan" element={<Scan />} />
              <Route path="/report" element={<Report />} />
              <Route path="/method" element={<Method />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/guides" element={<Guides />} />
              <Route path="/guides/:slug" element={<Guides />} />
              <Route path="/plan" element={<Plan />} />
              <Route path="/progress" element={<Progress />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/account" element={<Account />} />
              <Route path="/download" element={<Download />} />
              <Route path="/reset" element={<ResetPassword />} />
              <Route path="/verify" element={<VerifyEmail />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/legal/privacy" element={<PrivacyPolicy />} />
              <Route path="/legal/terms" element={<Terms />} />
              {/* Redirects from the pre-redesign URLs so old links still resolve. */}
              <Route path="/results" element={<Navigate to="/report" replace />} />
              <Route path="/science" element={<Navigate to="/method" replace />} />
              <Route path="/how-it-works" element={<Navigate to="/method" replace />} />
              <Route path="/privacy-first" element={<Navigate to="/privacy" replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
    </>
  );
}
